import { z } from 'zod'

/**
 * Elk zichtbaar tekstelement in een definitie is een paar {nl, en} (R-143,
 * ontwerp 4.4 en brief B13). Niet optioneel en niet per formulier maar per
 * element: label, hulptekst, keuzeoptie, regelmelding, sectietitel.
 *
 * Beide talen zijn verplicht en mogen niet leeg zijn. Dat is streng op deze
 * plek met opzet: publiceren blokkeert zonder uitzondering op een ontbrekende
 * vertaling (R-145, D-27), en een lege string die door het schema glipt is
 * precies de vorm die die blokkade later zou moeten vangen.
 *
 * De controle op witruimte staat als regex en niet als `refine`, omdat
 * `z.toJSONSchema` een refinement stil laat vallen (opdracht 7): dan zou het
 * gegenereerde JSON Schema zwakker zijn dan het Zod-schema zonder dat iemand
 * dat ziet.
 */
export const Text = z.strictObject({
  nl: z.string().min(1).regex(/\S/, 'de Nederlandse tekst mag niet alleen witruimte zijn'),
  en: z.string().min(1).regex(/\S/, 'de Engelse tekst mag niet alleen witruimte zijn'),
})

export type Text = z.infer<typeof Text>

/**
 * Hetzelfde taalpaar, zoals het eruitziet zolang de inrichter er nog aan werkt.
 *
 * Dit is de leaf van de conceptboom (ontwerp 8.1 punt 2): elke conceptstand staat
 * in `form_definition`, ook een stand die nog niet af is. Met `Tekst` kan dat niet.
 * Die eist beide talen niet-leeg, dus een veld waar alleen het Nederlandse label
 * staat past niet in het schema, en dan is er geen opslag voor werk in uitvoering.
 *
 * **Beide sleutels blijven verplicht en de waarde mag leeg zijn**, in plaats van de
 * sleutels optioneel te maken. Twee redenen. Er is dan één vorm voor "nog niet
 * ingevuld", de lege string, en niet twee waartussen niemand kan kiezen; en de vorm
 * van een concept is gelijk aan die van een definitie, dus `verhard` hoeft nooit een
 * sleutel te verzinnen die er niet stond. Een invoerveld in de browser levert
 * bovendien precies dit op zodra iemand het leegmaakt.
 *
 * Witruimte telt als leeg, maar dat staat hier niet: `'  '` is een geldige
 * conceptwaarde en een gat bij het publiceren. De controle daarop hoort bij de
 * publicatiepoort (R-145, D-27) en niet bij het opslaan, want anders kan de
 * inrichter zijn werk niet bewaren.
 */
export const TextDraft = z.strictObject({
  nl: z.string(),
  en: z.string(),
})

export type TextDraft = z.infer<typeof TextDraft>
